import { useState } from "react";
import { Avatar, Button, IconButton, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { NotificationsActiveOutlined, NotificationsNoneOutlined, ShareOutlined } from "@mui/icons-material";
import { ResponsivePie } from "@nivo/pie";
import { makeStyles } from "@/compat/muiStyles";
import { useSnackbar } from "notistack";
import CardBase from "../../../components/cards/CardBase";
import useRiskAssessmentHistoryAcknowledgementRecipients from "../../hooks/queries/useRiskAssessmentHistoryAcknowledgementRecipients";
import useShareRiskAssessmentHistory from "../../hooks/mutations/useShareRiskAssessmentHistory";

// The three acknowledgement statuses the API tracks (AcknowledgementStatus enum:
// Sent=0, Viewed=1, Acknowledged=2). Labels here are the user-facing wording
// ("Not opened"/"Viewed"/"Acknowledged") — kept in one place so they're trivial
// to change. Ordered Acknowledged -> Viewed -> Not opened to match the design.
const ACKNOWLEDGED = 2;
const STATUS_META = [
    { value: 2, key: "acknowledged", label: "Acknowledged", color: "#2e9e5b" },
    { value: 1, key: "viewed", label: "Viewed", color: "#5848c9" },
    { value: 0, key: "notOpened", label: "Not opened", color: "#e0a12f" },
];

const useStyles = makeStyles((theme) => ({
    donutWrapper: {
        position: "relative",
        height: 200,
    },
    donutOverlay: {
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "none",
    },
    donutTotal: {
        fontWeight: 700,
        lineHeight: 1,
    },
    legend: {
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: theme.spacing(1.5),
        marginTop: theme.spacing(1),
    },
    legendItem: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(0.5),
    },
    legendDot: {
        width: 10,
        height: 10,
        borderRadius: "50%",
        flexShrink: 0,
    },
    legendLabel: {
        fontSize: "0.8rem",
        color: theme.palette.text.secondary,
    },
    tabs: {
        minHeight: "auto",
        marginTop: theme.spacing(1),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    tab: {
        textTransform: "none",
        minHeight: 44,
        fontSize: "0.8rem",
    },
    list: {
        marginTop: theme.spacing(1),
        maxHeight: 280,
        overflowY: "auto",
    },
    row: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
        padding: theme.spacing(1, 0),
    },
    rowAvatar: {
        width: 32,
        height: 32,
        fontSize: "0.8rem",
        backgroundColor: theme.palette.primary.main,
    },
    rowEmail: {
        flex: 1,
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    emptyState: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: theme.spacing(1),
        padding: theme.spacing(3, 1),
        color: theme.palette.text.secondary,
    },
    tooltip: {
        background: theme.palette.grey[900],
        color: "#fff",
        padding: theme.spacing(0.5, 1),
        borderRadius: 4,
        fontSize: "0.75rem",
    },
}));

// Recipients only expose an email (no name), so derive a 2-letter avatar from the
// local part: split on . _ - and take the first letter of the first two tokens,
// else the first two characters.
const initialsFromEmail = (email = "") => {
    const local = email.split("@")[0] || email;
    const parts = local.split(/[._-]+/).filter(Boolean);
    const chars = parts.length > 1 ? parts[0][0] + parts[1][0] : local.slice(0, 2);
    return (chars || "?").toUpperCase();
};

export default function RiskAssessmentSharingPanel({ riskAssessmentHistoryId, onShare }) {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const [selectedStatus, setSelectedStatus] = useState(ACKNOWLEDGED);

    const recipientsQuery = useRiskAssessmentHistoryAcknowledgementRecipients(riskAssessmentHistoryId);
    const shareRiskAssessmentHistory = useShareRiskAssessmentHistory();

    // The shared axios instance unwraps response.data, so `data` is the recipient
    // array; guard anyway in case it hasn't loaded / errored (endpoint 400s until
    // the assessment has been shared at least once).
    const recipients = Array.isArray(recipientsQuery.data) ? recipientsQuery.data : [];
    const total = recipients.length;
    const countFor = (statusValue) => recipients.filter((r) => r.status === statusValue).length;
    const pendingEmails = recipients.filter((r) => r.status !== ACKNOWLEDGED).map((r) => r.email);
    const selectedRecipients = recipients.filter((r) => r.status === selectedStatus);

    const chartData = STATUS_META.map((s) => ({ id: s.key, label: s.label, value: countFor(s.value), color: s.color })).filter(
        (d) => d.value > 0
    );

    const isBusy = Boolean(shareRiskAssessmentHistory.isLoading);
    const isLoading = Boolean(riskAssessmentHistoryId) && recipientsQuery.isLoading;
    const isFetching = Boolean(riskAssessmentHistoryId) && recipientsQuery.isFetching;

    const sendReminder = (emails) => {
        const emailAddresses = (emails || []).filter(Boolean);
        if (!emailAddresses.length) return;
        shareRiskAssessmentHistory.mutate(
            { riskAssessmentHistoryId, emailAddresses, isReSend: true },
            {
                // A resend doesn't change the recipient list or their statuses, so there's
                // nothing to refetch — just confirm it was sent.
                onSuccess: () => enqueueSnackbar(emailAddresses.length > 1 ? "Reminders sent" : "Reminder sent", { variant: "default" }),
                onError: () => enqueueSnackbar("There was an error sending the reminder", { variant: "error" }),
            }
        );
    };

    const headerActions =
        total > 0 ? (
            <>
                {onShare && (
                    <Button size="small" variant="contained" color="primary" startIcon={<ShareOutlined />} onClick={onShare}>
                        Share
                    </Button>
                )}
                {pendingEmails.length > 0 && (
                    <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<NotificationsActiveOutlined />}
                        disabled={isBusy}
                        onClick={() => sendReminder(pendingEmails)}
                    >
                        Remind all pending
                    </Button>
                )}
            </>
        ) : null;

    const content = () => {
        if (total === 0)
            return (
                <div className={classes.emptyState}>
                    <ShareOutlined color="disabled" />
                    <Typography variant="body2">
                        {riskAssessmentHistoryId
                            ? "This risk assessment hasn't been shared with anyone yet."
                            : "Once this assessment has been completed you can share it and track acknowledgements here."}
                    </Typography>
                    {onShare && (
                        <Button variant="contained" color="primary" size="small" startIcon={<ShareOutlined />} onClick={onShare}>
                            Share
                        </Button>
                    )}
                </div>
            );

        return (
            <>
                <div className={classes.donutWrapper}>
                    <ResponsivePie
                        data={chartData}
                        colors={{ datum: "data.color" }}
                        margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
                        innerRadius={0.75}
                        padAngle={1}
                        cornerRadius={3}
                        enableArcLinkLabels={false}
                        enableArcLabels={false}
                        activeOuterRadiusOffset={6}
                        animate
                        tooltip={({ datum }) => (
                            <div className={classes.tooltip}>
                                {datum.data.label}: {datum.value}
                            </div>
                        )}
                    />
                    <div className={classes.donutOverlay}>
                        <Typography variant="h4" className={classes.donutTotal}>
                            {total}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            {total === 1 ? "person" : "people"}
                        </Typography>
                    </div>
                </div>

                <div className={classes.legend}>
                    {STATUS_META.map((s) => (
                        <div className={classes.legendItem} key={s.key}>
                            <span className={classes.legendDot} style={{ backgroundColor: s.color }} />
                            <span className={classes.legendLabel}>
                                {countFor(s.value)} {s.label}
                            </span>
                        </div>
                    ))}
                </div>

                <Tabs
                    className={classes.tabs}
                    value={selectedStatus}
                    onChange={(_, value) => setSelectedStatus(value)}
                    variant="fullWidth"
                    textColor="primary"
                    indicatorColor="primary"
                >
                    {STATUS_META.map((s) => (
                        <Tab key={s.key} className={classes.tab} value={s.value} label={`${s.label} (${countFor(s.value)})`} />
                    ))}
                </Tabs>

                <div className={classes.list}>
                    {selectedRecipients.length === 0 ? (
                        <Typography variant="caption" color="textSecondary">
                            No one in this group.
                        </Typography>
                    ) : (
                        selectedRecipients.map((r) => (
                            <div className={classes.row} key={r.id ?? r.email}>
                                <Avatar className={classes.rowAvatar}>{initialsFromEmail(r.email)}</Avatar>
                                <Typography variant="body2" className={classes.rowEmail} title={r.email}>
                                    {r.email}
                                </Typography>
                                {r.status !== ACKNOWLEDGED && (
                                    <Tooltip title="Send reminder">
                                        <span>
                                            <IconButton size="small" disabled={isBusy} onClick={() => sendReminder([r.email])}>
                                                <NotificationsNoneOutlined fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </>
        );
    };

    return (
        <CardBase title="Sharing" isLoading={isLoading} isFetching={isFetching} rightComponent={headerActions}>
            {content()}
        </CardBase>
    );
}
